import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {TEditableColumn} from 'components/editable-table';
import {IEntityMeta, selectMetadata} from 'entities/metadata';
import {TEntityValue} from 'modules/data';
import {EEntityType} from 'shared/constants/entities';
import { useEntityFromLocation } from 'shared/hooks/use-entity-from-location';
import {TAppState} from 'store/config/types';
import {
    IEntityFileUploadState,
    FILE_UNIQUE_FIELD_NAME,
    selectDocumentBranchNames,
    selectDocumentFileUpload,
    selectDocumentTypeOnUpload,
} from 'store/slices/documents-uploading-slice';
import {
    getRegionFromSubsectionCode,
    selectRegions,
    IRegionDto,
} from 'store/slices/regions-slice';

import {IEditFormArgs, generateFileColumnsByMetaData} from '../documents-file-upload-modal-utils';

export const useDocumentsUploadColumns = (
    entityName: string,
    fileSizeFormat: string | null,
    useUploadDate: boolean | undefined,
    handleDelete: (tableRow: Record<string, TEntityValue>) => void,
    handleEdit: (tableRow: Record<string, TEntityValue>, args?: (IEditFormArgs | undefined)) => void,
) => {
    const [columnsMeta, setColumnsMeta] = useState<TEditableColumn[] | undefined>();
    const [defaultRegion, setDefaultRegion] = useState<IRegionDto | undefined>();
    const {subsection} = useEntityFromLocation();

    const metadataSelector = selectMetadata(entityName, EEntityType.EDITABLE_TABLE);

    const {
        regions,
        documentTypesOnUpload,
        branchNames,
        fileUpload,
        metadata,
    } = useSelector((state: TAppState) => ({
        regions: selectRegions(state),
        documentTypesOnUpload: selectDocumentTypeOnUpload(state),
        branchNames: selectDocumentBranchNames(state),
        fileUpload: selectDocumentFileUpload(state),
        metadata: metadataSelector(state) as IEntityMeta,
    }));

    useEffect(() => {
        if (!regions || !Array.isArray(documentTypesOnUpload) || !Array.isArray(branchNames)) {
            return;
        }

        if (!defaultRegion) {
            const defaultRegionData = getRegionFromSubsectionCode(subsection, regions);
            setDefaultRegion(defaultRegionData);
        }

        let fields = metadata?.fields;

        // exclude uploadDate column if app settings says this
        if (useUploadDate === false) {
            fields = fields?.filter(item => item.key !== 'uploadDate');
        }

        const arr = fields?.map(item => generateFileColumnsByMetaData(
            item,
            regions,
            documentTypesOnUpload,
            branchNames,
            {
                handleDelete,
                handleEdit,
                fileSizeFormat,
                progress: {
                    fileUploadSlice: fileUpload[entityName] as IEntityFileUploadState,
                    fieldKey: FILE_UNIQUE_FIELD_NAME,
                },
            },
        ));

        setColumnsMeta(arr);
    }, [regions, documentTypesOnUpload, branchNames, fileUpload, fileSizeFormat, useUploadDate, subsection]);

    return {columnsMeta};
};
